import React from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface HistoryLineChartProps {
  data: Array<{ period: string; value: number }>;
  label: string;
  loading: boolean;
  color?: string;
}

const numberFormatter = new Intl.NumberFormat('pt-BR');

const formatPeriod = (period: string): string => {
  try {
    // period is 'YYYY-MM', parse as first day of month
    const date = parseISO(`${period}-01`);
    return format(date, 'MMM/yy', { locale: ptBR });
  } catch {
    return period;
  }
};

const HistoryLineChart: React.FC<HistoryLineChartProps> = ({
  data,
  label,
  loading,
  color,
}) => {
  const theme = useTheme();
  const lineColor = color ?? theme.palette.primary.main;

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: 1 }} />;
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
        <Typography variant="body1" color="text.secondary">
          Sem dados de histórico
        </Typography>
      </Box>
    );
  }

  const chartData = data.map((item) => ({
    period: formatPeriod(item.period),
    value: item.value,
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
        <XAxis
          dataKey="period"
          tick={{ fontSize: 12 }}
          stroke={theme.palette.text.secondary}
        />
        <YAxis
          tick={{ fontSize: 12 }}
          stroke={theme.palette.text.secondary}
          tickFormatter={(v: number) => numberFormatter.format(v)}
          width={60}
        />
        <Tooltip
          formatter={(value: any) => [numberFormatter.format(Number(value)), label]}
          labelStyle={{ fontWeight: 600 }}
        />
        <Line
          type="monotone"
          dataKey="value"
          name={label}
          stroke={lineColor}
          strokeWidth={2}
          dot={{ r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default HistoryLineChart;
